import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ArmadaNav } from "@/components/ArmadaNav";
import { RankBadge } from "@/components/Badges";
import {
  Flame, Coins, TrendingUp, TrendingDown, AlertTriangle, Anchor,
  Activity, Crown, Sparkles, ArrowDownRight, ArrowUpRight, Loader2,
} from "lucide-react";

const STATUS_COLOR = {
  DOMINANT:   { bg: "from-emerald-500/20 to-transparent", border: "border-emerald-500/50", text: "text-emerald-300", dot: "bg-emerald-400" },
  PROFITABLE: { bg: "from-emerald-500/15 to-transparent", border: "border-emerald-500/40", text: "text-emerald-300", dot: "bg-emerald-400" },
  "AT RISK":  { bg: "from-amber-500/15 to-transparent", border: "border-amber-500/40", text: "text-amber-300", dot: "bg-amber-400" },
  BURNING:    { bg: "from-red-500/15 to-transparent", border: "border-red-500/40", text: "text-red-300", dot: "bg-red-400" },
};

const $ = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

export default function TreasuryPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/treasury/expenses");
        setData(data);
      } catch {}
    })();
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen bg-armada-base">
        <ArmadaNav />
        <div className="flex items-center justify-center py-32 text-zinc-500">
          <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37] mr-2" /> Computing fleet treasury…
        </div>
      </div>
    );
  }

  const { fuel_burn, gold_harvest, metrics } = data;
  const color = STATUS_COLOR[metrics.status] || STATUS_COLOR.PROFITABLE;
  const profitable = metrics.net_profit >= 0;

  return (
    <div className="min-h-screen bg-armada-base">
      <ArmadaNav />
      <main className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10">
        <div className="mb-10 animate-fade-up">
          <div className="text-[10px] tracking-armada text-[#D4AF37] font-bold mb-2 flex items-center gap-2">
            <Coins className="w-3 h-3" /> FLEET TREASURY · FUEL BURN VS GOLD HARVEST
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-white">
            The <span className="gold-shimmer">Ledger</span>
          </h1>
          <p className="text-sm text-zinc-400 mt-3 font-light max-w-2xl">
            Real-time operational cost vs revenue intelligence. Every recruit, every hull, every transaction — instrumented.
          </p>
        </div>

        {/* Status hero card */}
        <div
          data-testid="treasury-status-hero"
          className={`mb-6 rounded-xl p-7 border ${color.border} bg-gradient-to-br ${color.bg} animate-fade-up stagger-1 relative overflow-hidden`}
          style={{ backdropFilter: "blur(28px) saturate(180%)", background: "rgba(10,10,10,0.65)" }}
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className={`flex items-center gap-2 text-[10px] tracking-armada font-bold mb-2 ${color.text}`}>
                <span className="relative flex h-2 w-2">
                  <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${color.dot} opacity-75`} />
                  <span className={`relative inline-flex rounded-full h-2 w-2 ${color.dot}`} />
                </span>
                FLEET STATUS · {metrics.status}
              </div>
              <div className="flex items-baseline gap-3">
                <span className={`text-5xl md:text-6xl font-black tracking-tight ${profitable ? "text-emerald-300" : "text-red-300"}`}>
                  {profitable ? "+" : ""}{$(metrics.net_profit)}
                </span>
                <span className="text-zinc-500 text-xs uppercase tracking-wider">Net Profit</span>
              </div>
              <div className={`mt-2 text-sm font-bold flex items-center gap-1 ${profitable ? "text-emerald-300" : "text-red-300"}`}>
                {profitable ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                {metrics.profitability_pct.toFixed(1)}% margin
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 min-w-[260px]">
              <Metric label="Burn Rate" value={metrics.burn_rate_ratio !== null ? `${metrics.burn_rate_ratio}×` : "—"}
                hint={metrics.burn_rate_ratio === null ? "no 30d revenue" : metrics.burn_rate_ratio < 1 ? "below revenue" : "above revenue"}
                good={metrics.burn_rate_ratio === null ? null : metrics.burn_rate_ratio < 1} />
              <Metric label="Runway" value={metrics.runway_months !== null ? `${metrics.runway_months}mo` : "∞"}
                hint="treasury / monthly burn" good={metrics.runway_months === null || metrics.runway_months > 6} />
            </div>
          </div>
        </div>

        {/* Burn vs Harvest grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Fuel Burn */}
          <div data-testid="treasury-fuel-burn" className="glass-strong rounded-xl p-6 animate-fade-up stagger-2 border border-red-500/20">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[10px] tracking-armada text-red-300 font-bold">FUEL BURN · MONTHLY</div>
                <div className="text-lg font-bold text-white mt-1">Operational Cost</div>
              </div>
              <Flame className="w-6 h-6 text-red-400" />
            </div>
            <div className="text-5xl font-black text-red-300 mb-4 tracking-tight">
              {$(fuel_burn.monthly_total)}
            </div>
            <div className="space-y-2.5 mb-4">
              {fuel_burn.breakdown.filter((b) => b.count > 0).map((b) => (
                <div key={b.rank} className="flex items-center justify-between py-2 border-b border-white/5">
                  <div className="flex items-center gap-3">
                    <RankBadge rank={b.rank} />
                    <span className="text-xs text-zinc-500 font-mono">{b.count} × ${b.rate_per_unit}</span>
                  </div>
                  <span className="text-white font-bold">{$(b.subtotal)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between py-2 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <Anchor className="w-4 h-4 text-zinc-500" />
                  <span className="text-xs text-zinc-400">Hull overhead · {fuel_burn.active_hulls} active hulls × 5%</span>
                </div>
                <span className="text-white font-bold">{$(fuel_burn.hull_overhead)}</span>
              </div>
            </div>
            <div className="text-[11px] text-zinc-500 font-mono pt-2 border-t border-red-500/20">
              Nebula ${fuel_burn.rates.Nebula} · Pulsar ${fuel_burn.rates.Pulsar} · Quasar ${fuel_burn.rates.Quasar} · Celestial ${fuel_burn.rates.Celestial} / month
            </div>
          </div>

          {/* Gold Harvest */}
          <div data-testid="treasury-gold-harvest" className="glass-strong rounded-xl p-6 animate-fade-up stagger-3 border border-[#D4AF37]/30">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[10px] tracking-armada text-[#D4AF37] font-bold">GOLD HARVEST · LIFETIME</div>
                <div className="text-lg font-bold text-white mt-1">Gross Revenue</div>
              </div>
              <Sparkles className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <div className="text-5xl font-black gold-shimmer mb-4 tracking-tight">
              {$(gold_harvest.gross_revenue)}
            </div>
            <div className="space-y-2.5 mb-4">
              <HarvestRow icon={Crown} label="Sovereign Cut" value={gold_harvest.sovereign_cut} accent="text-[#D4AF37]" />
              <HarvestRow icon={Activity} label="Director Cut" value={gold_harvest.director_cut} accent="text-white" />
              <HarvestRow icon={Coins} label="Commander Cut" value={gold_harvest.commander_cut} accent="text-zinc-200" />
              <HarvestRow icon={Sparkles} label="Heir Fund · Liquid" value={gold_harvest.heir_fund} accent="text-rose-200" />
              <HarvestRow icon={TrendingUp} label="Fleet Treasury · Shop" value={gold_harvest.fleet_treasury} accent="text-emerald-300" />
            </div>
            <div className="text-[11px] text-zinc-500 font-mono pt-2 border-t border-[#D4AF37]/20 flex justify-between">
              <span>{gold_harvest.transaction_count} transactions</span>
              <span>30D: {$(gold_harvest.revenue_30d)}</span>
            </div>
          </div>
        </div>

        {/* Director Cut spotlight */}
        <div className="glass-strong rounded-xl p-6 animate-fade-up stagger-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-[10px] tracking-armada text-zinc-400 font-bold">DIRECTOR'S TAKE · POST HEIR-TAX</div>
              <div className="text-lg font-bold text-white mt-1">Personal Yield</div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-black gold-shimmer">{$(gold_harvest.director_cut)}</div>
              <div className="text-[11px] text-zinc-500 mt-1">
                vs <span className="text-red-300">{$(fuel_burn.monthly_total)}</span> monthly burn ·
                ratio <span className={gold_harvest.director_cut >= fuel_burn.monthly_total ? "text-emerald-300" : "text-amber-300"}>
                  {fuel_burn.monthly_total > 0 ? (gold_harvest.director_cut / fuel_burn.monthly_total).toFixed(2) : "∞"}×
                </span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function Metric({ label, value, hint, good }) {
  const colorClass = good === null ? "text-zinc-300" : good ? "text-emerald-300" : "text-amber-300";
  return (
    <div className="p-4 rounded-lg bg-black/50 border border-white/10">
      <div className="text-[10px] tracking-wider text-zinc-500 uppercase font-bold">{label}</div>
      <div className={`text-2xl font-black mt-1 ${colorClass}`}>{value}</div>
      {hint && <div className="text-[10px] text-zinc-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function HarvestRow({ icon: Icon, label, value, accent }) {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5">
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${accent}`} />
        <span className="text-xs text-zinc-400">{label}</span>
      </div>
      <span className={`font-bold ${accent}`}>{$(value)}</span>
    </div>
  );
}
